import { Spacing, Text, Title } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { platform } from 'reactApp/appHelpers/configHelpers';
import { overquota256TariffId } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbClearDownload';
import { QUOTA_PAGE_BASE_URL } from 'reactApp/constants/magicIdentificators';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getOverQuota } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendDwh, sendXray } from 'reactApp/utils/ga';

import styles from './MobileDisableDownloadPopup.css';

const sendRadar = (action: 'show' | 'click' | 'close' = 'show') => {
    sendXray(['overq_dwnld_touchtip', action]);

    sendDwh({
        eventCategory: 'tooltip',
        action,
        dwhData: {
            name: 'mnz_overquota_cleaner',
            platform,
        },
        sendImmediately: true,
    });
};

type Props = {
    onClose?: () => void;
};

export const MobileDisableDownloadPopup = memo<Props>(({ onClose }) => {
    const dispatch = useDispatch();
    const scrollRef = useRef<HTMLDivElement>(null);
    const quota = useSelector(getOverQuota);
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, overquota256TariffId));

    useEffect(sendRadar, []);

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose?.();
    }, [onClose]);

    const handleClick = useCallback(() => {
        sendRadar('click');

        if (!product) {
            dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL }));
            return;
        }

        dispatch(
            openTariffBuy({
                productId: product.id,
                onSuccess: () => location.reload(),
                isQuotaCleaner: true,
                isMobile: true,
                paySource: getPaySource('tooltip_overquota_cleaner'),
            })
        );

        onClose?.();
    }, [onClose, product]);

    return (
        <MobileDialog
            id="cleaner-download-disable-ab"
            onClose={handleClose}
            closeOnDimmerClick
            topmost
            scrollRef={scrollRef}
            mod="base"
            contextPadding="zero"
        >
            <div ref={scrollRef} className={styles.root}>
                <Title level="2">Скачать файлы пока нельзя</Title>
                <Spacing size={12} />
                <Text className={styles.text}>
                    Облако переполнено на <span className={styles.space}>{quota.value}</span>. Увеличьте место с подпиской Mail Space или
                    удалите ненужные файлы
                </Text>
                <Spacing size={24} />
                <Button theme="vk" primary sizeMode={ButtonSizeMode.big} onClick={handleClick}>
                    Увеличить место
                </Button>
            </div>
        </MobileDialog>
    );
});

MobileDisableDownloadPopup.displayName = 'MobileDisableDownloadPopup';
